/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  InputAdornment,
  SelectChangeEvent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import { StudioRole } from '../../../types/studio';
import {
  MemberStatus,
  MemberSearchBarProps,
} from '../../../types/studioMembers';

const MemberSearchBar = ({
  searchTerm,
  selectedRole,
  selectedStatus,
  onSearchChange,
  onRoleFilter,
  onStatusFilter,
  onClearFilters,
}: MemberSearchBarProps) => {
  const { t } = useTranslation('studio');
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  // Debounced search handler
  const handleSearchChange = useCallback(
    (value: string) => {
      setLocalSearchTerm(value);
      const timeoutId = setTimeout(() => {
        onSearchChange(value);
      }, 300);
      return () => clearTimeout(timeoutId);
    },
    [onSearchChange]
  );

  const handleRoleChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    onRoleFilter(value === '' ? undefined : (value as StudioRole));
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    onStatusFilter(value === '' ? undefined : (value as MemberStatus));
  };

  const handleClearSearch = () => {
    setLocalSearchTerm('');
    onSearchChange('');
  };

  const hasActiveFilters = selectedRole || selectedStatus || searchTerm;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        flexWrap: 'wrap',
        mb: 3,
      }}
    >
      {/* Search Field */}
      <TextField
        placeholder={t('memberSearch.placeholder')}
        value={localSearchTerm}
        onChange={e => handleSearchChange(e.target.value)}
        variant="outlined"
        size="small"
        sx={{ minWidth: 250, flexGrow: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: localSearchTerm && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClearSearch}
                aria-label={t('memberSearch.clearSearch')}
              >
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Role Filter */}
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel id="role-filter-label">Role</InputLabel>
        <Select
          labelId="role-filter-label"
          value={selectedRole || ''}
          label="Role"
          onChange={handleRoleChange}
        >
          <MenuItem value="">{t('memberSearch.allRoles')}</MenuItem>
          <MenuItem value={StudioRole.OWNER}>{t('roles.owner')}</MenuItem>
          <MenuItem value={StudioRole.MANAGER}>{t('roles.manager')}</MenuItem>
          <MenuItem value={StudioRole.ADMIN}>{t('roles.admin')}</MenuItem>
          <MenuItem value={StudioRole.MEMBER}>{t('roles.member')}</MenuItem>
        </Select>
      </FormControl>

      {/* Status Filter */}
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel id="status-filter-label">Status</InputLabel>
        <Select
          labelId="status-filter-label"
          value={selectedStatus || ''}
          label="Status"
          onChange={handleStatusChange}
        >
          <MenuItem value="">{t('memberSearch.allStatuses')}</MenuItem>
          <MenuItem value={MemberStatus.ACTIVE}>
            {t('members.status.active')}
          </MenuItem>
          <MenuItem value={MemberStatus.INACTIVE}>
            {t('members.status.inactive')}
          </MenuItem>
          <MenuItem value={MemberStatus.PENDING}>
            {t('members.status.pending')}
          </MenuItem>
          <MenuItem value={MemberStatus.SUSPENDED}>
            {t('members.status.suspended')}
          </MenuItem>
        </Select>
      </FormControl>

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <IconButton
          onClick={onClearFilters}
          color="primary"
          aria-label={t('memberSearch.clearFilters')}
          title={t('memberSearch.clearFilters')}
        >
          <FilterIcon />
        </IconButton>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {selectedRole && (
            <Chip
              label={`${t('memberSearch.roleFilter')}: ${t(`roles.${selectedRole.toLowerCase()}`)}`}
              size="small"
              onDelete={() => onRoleFilter(undefined)}
              color="primary"
              variant="outlined"
            />
          )}
          {selectedStatus && (
            <Chip
              label={`${t('memberSearch.statusFilter')}: ${t(`members.status.${selectedStatus.toLowerCase()}`)}`}
              size="small"
              onDelete={() => onStatusFilter(undefined)}
              color="primary"
              variant="outlined"
            />
          )}
          {searchTerm && (
            <Chip
              label={`${t('memberSearch.searchFilter')}: "${searchTerm}"`}
              size="small"
              onDelete={handleClearSearch}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default MemberSearchBar;
